import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  CARRIER_CODE_TYPES,
  CarrierCodeDto,
  CarrierCodeInput,
  CarrierDto,
  CarrierInput,
  Paged,
  PlanDto,
  PlanInput,
  PlanStateStatus,
  TopupDto,
  TopupInput,
  carrierCodeUpdateSchema,
  carrierUpdateSchema,
  topupUpdateSchema,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/** Plan Config (PC232) server calls. Carrier and plan lookups live in `lookups.ts`. */

type CarrierUpdate = z.input<typeof carrierUpdateSchema>;
type CarrierCodeUpdate = z.input<typeof carrierCodeUpdateSchema>;
type TopupUpdate = z.input<typeof topupUpdateSchema>;

/**
 * Query keys. `['plans', ...]` and `['carriers']` are shared with `lookups.ts`, so a plan or
 * carrier write also refreshes the selects other screens build from them.
 */
const KEYS = {
  plans: ['plans'],
  carriers: ['carriers'],
  carrierCodes: ['carrier-codes'],
  topups: ['topups'],
} as const;

/* ---------------------------------- Plans --------------------------------- */

export const usePlans = (params: Query) =>
  useQuery({
    queryKey: ['plans', 'list', params],
    queryFn: () => api.get<Paged<PlanDto>>('/plans', params),
    placeholderData: (prev) => prev,
  });

/**
 * One plan, for the edit form. Not kept in the cache and not refetched in the background:
 * the form is filled from this once, and a stale or late copy would overwrite what the user typed.
 */
export const usePlan = (id: string | undefined) =>
  useQuery({
    queryKey: ['plans', 'one', id],
    queryFn: () => api.get<PlanDto>(`/plans/${id}`),
    enabled: Boolean(id),
    gcTime: 0,
    refetchOnReconnect: false,
  });

/** Link target for the "Download" button: the CSV export with the applied filters. */
export const plansExportUrl = (filters: Query) => api.url('/plans/export', filters);

function usePlanMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: KEYS.plans }),
        // "Plans" count on the Carrier Details tab.
        queryClient.invalidateQueries({ queryKey: KEYS.carrierCodes }),
      ]),
  });
}

export const useCreatePlan = () =>
  usePlanMutation((input: PlanInput) => api.post<PlanDto>('/plans', input));
export const useUpdatePlan = () =>
  usePlanMutation(({ id, input }: { id: string; input: PlanInput }) =>
    api.put<PlanDto>(`/plans/${id}`, input),
  );
export const useRenamePlan = () =>
  usePlanMutation(({ id, name }: { id: string; name: string }) =>
    api.patch<PlanDto>(`/plans/${id}/name`, { name }),
  );
export const useSetPlanStateStatus = () =>
  usePlanMutation(({ id, state, status }: { id: string; state: string; status: PlanStateStatus }) =>
    api.patch<PlanDto>(`/plans/${id}/states/${state}`, { status }),
  );

/* -------------------------------- Carriers -------------------------------- */

function useCarrierMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: KEYS.carriers }),
        // Carrier names and codes are shown on every list of this screen.
        queryClient.invalidateQueries({ queryKey: KEYS.carrierCodes }),
        queryClient.invalidateQueries({ queryKey: KEYS.plans }),
        queryClient.invalidateQueries({ queryKey: KEYS.topups }),
      ]),
  });
}

export const useCreateCarrier = () =>
  useCarrierMutation((input: CarrierInput) => api.post<CarrierDto>('/carriers', input));
export const useUpdateCarrier = () =>
  useCarrierMutation(({ id, input }: { id: string; input: CarrierUpdate }) =>
    api.patch<CarrierDto>(`/carriers/${id}`, input),
  );

/* ------------------------------ Carrier codes ----------------------------- */

export interface CarrierCodeOption {
  id: string;
  /** The wholesale plan code. */
  code: string;
  /** Code of the carrier it belongs to. */
  carrierCode: string;
  type: (typeof CARRIER_CODE_TYPES)[number];
}

export const useCarrierCodes = (params: Query) =>
  useQuery({
    queryKey: ['carrier-codes', 'list', params],
    queryFn: () => api.get<Paged<CarrierCodeDto>>('/carrier-codes', params),
    placeholderData: (prev) => prev,
  });

/** Active carrier codes for the "Carrier Setup" select on the plan form. */
export const useCarrierCodeOptions = (enabled = true) =>
  useQuery({
    queryKey: ['carrier-codes', 'options'],
    queryFn: () => api.get<{ data: CarrierCodeOption[] }>('/carrier-codes/options'),
    select: (r) => r.data,
    enabled,
  });

function useCarrierCodeMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: KEYS.carrierCodes }),
        // Plans show their carrier code.
        queryClient.invalidateQueries({ queryKey: KEYS.plans }),
      ]),
  });
}

export const useCreateCarrierCode = () =>
  useCarrierCodeMutation((input: CarrierCodeInput) =>
    api.post<CarrierCodeDto>('/carrier-codes', input),
  );
export const useUpdateCarrierCode = () =>
  useCarrierCodeMutation(({ id, input }: { id: string; input: CarrierCodeUpdate }) =>
    api.patch<CarrierCodeDto>(`/carrier-codes/${id}`, input),
  );

/* --------------------------------- Top-ups -------------------------------- */

export const useTopups = (params: Query) =>
  useQuery({
    queryKey: ['topups', 'list', params],
    queryFn: () => api.get<Paged<TopupDto>>('/topups', params),
    placeholderData: (prev) => prev,
  });

export const topupsExportUrl = (filters: Query) => api.url('/topups/export', filters);

function useTopupMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.topups }),
  });
}

export const useCreateTopup = () =>
  useTopupMutation((input: TopupInput) => api.post<TopupDto>('/topups', input));
export const useUpdateTopup = () =>
  useTopupMutation(({ id, input }: { id: string; input: TopupUpdate }) =>
    api.patch<TopupDto>(`/topups/${id}`, input),
  );
